import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Text,
  Flex,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Progress,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiUsers, FiCheckCircle, FiClock, FiAlertCircle } from 'react-icons/fi';
import adminService from '../../services/api/adminService';

/**
 * Dashboard component
 * Admin dashboard with statistics and overview
 */
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Fetch statistics on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await adminService.getNominationStats();
        setStats(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('فشل في تحميل الإحصائيات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Show loading spinner
  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </Container>
    );
  }

  // Show error message
  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  // Return early if no stats
  if (!stats) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="info">
          <AlertIcon />
          لا توجد إحصائيات متاحة حالياً.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading as="h1" size="lg" mb={2}>
          لوحة التحكم
        </Heading>
        <Text color="gray.600">
          نظرة عامة على إحصائيات نظام الترشيح
        </Text>
      </Box>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="blue.50"
                borderRadius="full"
                color="blue.500"
                me={4}
              >
                <Icon as={FiUsers} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>إجمالي المرشحين</StatLabel>
                <StatNumber>{stats.total_nominations || 0}</StatNumber>
                <StatHelpText>
                  <StatArrow type={(stats.weekly_growth || 0) >= 0 ? "increase" : "decrease"} />
                  {Math.abs(stats.weekly_growth || 0)}% هذا الأسبوع
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="green.50"
                borderRadius="full"
                color="green.500"
                me={4}
              >
                <Icon as={FiCheckCircle} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>المرشحون المقبولون</StatLabel>
                <StatNumber>{stats.by_status?.approved || 0}</StatNumber>
                <StatHelpText>
                  {stats.total_nominations > 0 ? Math.round(((stats.by_status?.approved || 0) / stats.total_nominations) * 100) : 0}% من الإجمالي
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="yellow.50"
                borderRadius="full"
                color="yellow.500"
                me={4}
              >
                <Icon as={FiClock} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>قيد الانتظار</StatLabel>
                <StatNumber>{stats.by_status?.pending || 0}</StatNumber>
                <StatHelpText>
                  {stats.total_nominations > 0 ? Math.round(((stats.by_status?.pending || 0) / stats.total_nominations) * 100) : 0}% من الإجمالي
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="red.50"
                borderRadius="full"
                color="red.500"
                me={4}
              >
                <Icon as={FiAlertCircle} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>المرشحون المرفوضون</StatLabel>
                <StatNumber>{stats.by_status?.rejected || 0}</StatNumber>
                <StatHelpText>
                  {stats.total_nominations > 0 ? Math.round(((stats.by_status?.rejected || 0) / stats.total_nominations) * 100) : 0}% من الإجمالي
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Branch Stats and Recent Activity */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        <GridItem>
          <Card>
            <CardHeader>
              <Heading size="md">إحصائيات الفروع</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                {stats.by_branch && stats.by_branch.length > 0 ? (
                  stats.by_branch.map((branch, index) => (
                    <Box key={index}>
                      <Flex justify="space-between" mb={1}>
                        <Text>{branch.name}</Text>
                        <Text fontWeight="bold">{branch.count} مرشح</Text>
                      </Flex>
                      <Progress
                        value={branch.percentage}
                        colorScheme={index === 0 ? 'blue' : index === 1 ? 'green' : 'purple'}
                        borderRadius="full"
                        size="sm"
                      />
                    </Box>
                  ))
                ) : (
                  <Text color="gray.500" textAlign="center">
                    لا توجد بيانات فروع متاحة
                  </Text>
                )}
              </Stack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardHeader>
              <Heading size="md">النشاط الأخير</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={4} divider={<Box borderBottomWidth="1px" borderColor={borderColor} />}>
                {stats.recent_activities && stats.recent_activities.length > 0 ? (
                  stats.recent_activities.map((activity, index) => (
                    <Flex key={index} justify="space-between">
                      <Box>
                        <Text fontWeight="medium">{activity.action}</Text>
                        <Text fontSize="sm" color="gray.500">{activity.date} - {activity.time}</Text>
                        {activity.candidate_name && (
                          <Text fontSize="xs" color="gray.400">({activity.candidate_name})</Text>
                        )}
                      </Box>
                    </Flex>
                  ))
                ) : (
                  <Text color="gray.500" textAlign="center">
                    لا يوجد نشاط أخير
                  </Text>
                )}
              </Stack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </Container>
  );
};

export default Dashboard;
